<template>
  <div class="i-campaign-roadmap">
    <div class="header">
      <div>{{ props.header }}</div>
      <div class="sub">{{ props.sub }}</div>
    </div>

    <div class="slider-testimonials">
      <template v-if="isDesktop">
        <iSliderTestimonials
          :testimonials="TESTIMONIALS"
          force-slim
          @clicked="(testimonialVideo: string) => (data.lightBoxVideo = testimonialVideo)"
        />
      </template>
      <template v-else>
        <iBaseAnimatedSlider
          sliding-direction="left"
          animation-duration-secs="25"
        >
          <iSliderTestimonials
            :testimonials="[...TESTIMONIALS, ...TESTIMONIALS, ...TESTIMONIALS]"
            force-slim
            @clicked="(testimonialVideo: string) => (data.lightBoxVideo = testimonialVideo)"
          />
        </iBaseAnimatedSlider>
      </template>
    </div>

    <iBaseAnimatedSlider
      sliding-direction="right"
      animation-duration-secs="210"
    >
      <iSliderReviews :reviews="REVIEWS_1" />
    </iBaseAnimatedSlider>

    <iBaseAnimatedSlider
      sliding-direction="right"
      animation-duration-secs="140"
    >
      <iSliderReviews :reviews="REVIEWS_2" />
    </iBaseAnimatedSlider>

    <iAssignedCoachDetails
      class="coach-card"
      :coach="props.coach"
      :show-help="false"
      has-custom-on-click
      hide-mobile-ctas
      @click="$emit('ctaClicked')"
    />

    <div class="inclusion">
      <div class="inner">
        <div class="details">
          <div class="top">
            <div class="discount">Exclusive Discount</div>
            <div class="caption">{{ props.productTitle }}<span class="grey"> (60-min)</span></div>
            <div class="subtitle">
              <div class="pulse-circle"></div>
              <strong>{{ openSlots }} slots</strong> left with exclusive discount
            </div>
            <div class="price">${{ props.price }} <span class="blue">$199.90</span></div>
          </div>
          <iBaseDelimiter :color="astronaut100" />
          <div class="bottom">
            <div class="include">Includes:</div>
            <div
              v-for="item in props.productInfo"
              :key="item"
              class="list-item"
            >
              <img :src="iconCheck" /> {{ item }}
            </div>
          </div>
        </div>
        <div class="cta">
          <iBaseButton
            v-bind="BUTTON_STYLE"
            text="Check Availability"
            @click="$emit('ctaClicked')"
          />
          <div class="slots">
            <div class="pulse-circle"></div>
            <strong>{{ openSlots }} slots</strong> left with exclusive discount
          </div>
        </div>
      </div>
    </div>

    <template v-if="data.lightBoxVideo">
      <iBaseLightBox @close="data.lightBoxVideo = ''">
        <video
          :src="data.lightBoxVideo"
          autoplay
          controls
          class="light-box-video"
          type="video/mp4"
        ></video>
      </iBaseLightBox>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useUserStore } from "@/stores/user"
import { reactive } from "vue"

import { astronaut100, azureRadiance200, azureRadiance700, white } from "@/styles/colors"
import { breakpointMd } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"
import useRandomNumber from "@/composables/useRandomNumber"

import iBaseAnimatedSlider from "@/components/base/BaseAnimatedSlider.vue"
import iBaseButton from "@/components/base/BaseButton.vue"
import iBaseDelimiter from "@/components/base/BaseDelimiter.vue"
import iBaseLightBox from "@/components/base/BaseLightBox.vue"
import iAssignedCoachDetails from "@/components/coaching/AssignedCoachDetails.vue"
import iSliderReviews from "@/components/global/SliderReviews.vue"
import iSliderTestimonials from "@/components/global/SliderTestimonials.vue"

// TYPES
import { CoachForSelection } from "@/shared/types/Coach"

// ASSETS
import iconCheck from "@/assets/icons/icon-blue-gray-check.svg"

// STORES
const userStore = useUserStore()

// EMITS
defineEmits(["ctaClicked"])

const props = defineProps({
  price: {
    type: String,
    required: true
  },
  header: {
    type: String,
    required: true
  },
  sub: {
    type: String,
    required: true
  },
  productTitle: {
    type: String,
    required: true
  },
  productInfo: {
    type: Array as () => string[],
    required: true
  },
  coach: {
    type: Object as () => CoachForSelection | null,
    default: null
  }
})

// CONSTANTS
const TESTIMONIALS = [
  {
    gifUrl:
      "https://jobtestorg-misc.s3.us-east-2.amazonaws.com/app-assets/coaching-testimonials/Julie-Ariel-Testimonial-102220241-gif.gif",
    videoUrl:
      "https://jobtestorg-misc.s3.us-east-2.amazonaws.com/app-assets/coaching-testimonials/Julie-Ariel-Testimonial-10222024.mp4"
  },
  {
    gifUrl:
      "https://jobtestorg-misc.s3.us-east-2.amazonaws.com/app-assets/coaching-testimonials/Linda-Laura-edited-09302024-approved-gif.gif",
    videoUrl:
      "https://jobtestorg-misc.s3.us-east-2.amazonaws.com/app-assets/coaching-testimonials/Linda-Laura-edited-09302024-approved.mp4"
  },
  {
    gifUrl:
      "https://jobtestorg-misc.s3.us-east-2.amazonaws.com/app-assets/coaching-testimonials/Marlo-Caroline-edited-10032024-approved-gif.gif",
    videoUrl:
      "https://jobtestorg-misc.s3.us-east-2.amazonaws.com/app-assets/coaching-testimonials/Marlo-Caroline-edited-10032024-approved.mp4"
  }
]
const REVIEWS_1 = [
  {
    ago: "1 week ago",
    review:
      "The coaching session was incredibly thorough, diving into various aspects that really matter for finding a fulfilling career path. I loved that the session focused not only on my interests and strengths but also considered fields I'm not interested in, ensuring our discussions were relevant to my career goals. This personalized approach really helped me gain clarity on which careers suit me best.",
    reviewer: "Ryan Watters",
    score: 5,
    title: "Guided Career Clarity"
  },
  {
    ago: "2 weeks ago",
    review:
      "Having used multiple career services over the years, I found this assessment and my hour of coaching to be the most impactful by far, even compared to much pricier options. Michelle was exceptional—her guidance was a perfect blend of strategic insights and actionable steps I could implement right away. Her supportive demeanor and expertise made a world of difference. Highly recommend!",
    reviewer: "Michael Mielke",
    score: 5,
    title: "Top-Tier Career Guidance That Outshines the Rest"
  },
  {
    ago: "3 weeks ago",
    review:
      "This coaching experience was everything I needed to understand my career journey. The insights from my session with Jen felt transformative, and she provided actionable steps to help me feel more fulfilled, challenged, and compensated in my work. Jen's structured approach, combined with her ability to help me focus on my true needs, was invaluable. Easily the best career investment I've made!",
    reviewer: "Luann Donnelly",
    score: 5,
    title: "Finally, Career Coaching That Drives Real Action!"
  },
  {
    ago: "4 weeks ago",
    review:
      "As I looked to transition careers, taking the test and working with Candace Barr provided the guidance I needed. She helped me identify career paths aligned with my personality, equipped me with useful research resources, and laid out a clear roadmap for my journey. Her support has been incredibly reassuring in this transition phase.",
    reviewer: "Phillip G",
    score: 5,
    title: "Excellent Support for Career Transition"
  },
  {
    ago: "1 month ago",
    review:
      "I would recommend Jobtest to anyone seeking career guidance. The career test provided direction, and my session with Corey Bott took it further by offering specific guidance and resources that have helped me clarify my next steps. Corey's advice has been a true asset in helping me understand which career path aligns with my strengths and values.",
    reviewer: "Grant Glasson",
    score: 5,
    title: "A Path to Career Fulfillment"
  },
  {
    ago: "2 months ago",
    review:
      "I stumbled across Jobtest during my career research, and I'm so glad I did! I've wanted to change careers for a while, and after taking the test, I signed up for biweekly coaching with Jen. Jen's kind, supportive approach has provided the confidence and direction I needed to pursue a career in interior design. Each session, she celebrates my progress and shares valuable resources, which have been essential in keeping me motivated and on track. I couldn't have asked for a better guide in my career shift!",
    reviewer: "Christine",
    score: 5,
    title: "Support and Accountability to Pursue My Dream Career"
  }
]
const REVIEWS_2 = [
  {
    ago: "5 days ago",
    review:
      "This coaching session was exactly what I needed. I was feeling really lost about my career direction, but my coach took the time to understand my goals, strengths, and even my hesitations. She helped me focus on fields where I can thrive and offered practical first steps. For the first time, I feel like I have a career path that's both achievable and exciting.",
    reviewer: "Alex Monroe",
    score: 5,
    title: "Finally Found My Career Path"
  },
  {
    ago: "1 week ago",
    review:
      "I've tried other career services, but Jobtest really set itself apart. The initial assessment was spot on, and Emma, my coach, genuinely cared about helping me move forward. She didn't just give me general advice—she provided real, actionable steps I could start on right away, which felt empowering. It's great to work with someone who really 'gets' what I'm going through.",
    reviewer: "Taylor Reese",
    score: 5,
    title: "The Guidance I've Been Looking For"
  },
  {
    ago: "2 weeks ago",
    review:
      "I was at a standstill in my career, feeling unfulfilled and unsure of my next move. The assessment and coaching session helped me recognize new opportunities that match my interests and values. My coach, Sam, was supportive and laid out a step-by-step plan that actually feels doable. I'm leaving this experience with a renewed sense of purpose.",
    reviewer: "Jordan Lee",
    score: 5,
    title: "Back on Track and Motivated"
  },
  {
    ago: "3 weeks ago",
    review:
      "Changing careers seemed so daunting before I came across Jobtest. The assessment gave me insight into careers that fit who I am, and Rachel, my coach, was a great listener. She showed me how to make use of the skills I already have to explore a new field. I feel much more hopeful, and I have a solid plan to work towards.",
    reviewer: "Morgan Foster",
    score: 5,
    title: "Guidance That Made Career Change Possible"
  },
  {
    ago: "1 month ago",
    review:
      "I've been at a crossroads for a while, feeling like I needed a bigger challenge but unsure where to start. My coach, Jenna, helped me identify meaningful career paths and reminded me of my strengths. She gave me clear steps to make things happen, and for the first time in years, I feel energized about my future. This has been a game-changer.",
    reviewer: "Patricia Carter",
    score: 5,
    title: "Rediscovered My Ambition"
  },
  {
    ago: "2 months ago",
    review:
      "I was skeptical about career coaching, but after working with Dylan, I wish I had done this sooner. He understood my challenges and provided specific, relevant advice. His encouragement helped me feel confident enough to start making the changes I need. Jobtest has been instrumental in helping me feel ready for a new chapter.",
    reviewer: "Chris Alvarez",
    score: 5,
    title: "The Push I Needed to Move Forward"
  }
]
const BUTTON_STYLE = {
  backgroundColor: azureRadiance700,
  borderRadius: "8px",
  fontWeight: "500",
  size: "medium",
  textColor: white,
  width: "100%",
  pulseColor: azureRadiance200,
  padding: "16px 24px"
}

// DATA
const data = reactive({
  lightBoxVideo: ""
})

//COMPOSABLES
const isDesktop = useIsScreenSize(breakpointMd)
const openSlots = useRandomNumber(userStore.getFullName || "Full Name", 3, 5)
</script>

<style scoped lang="scss">
.i-campaign-roadmap {
  padding: 40px 0 0;

  .header {
    @include display-xs;

    font-weight: 700;
    text-align: center;

    @media screen and (min-width: $breakpoint-sm) {
      @include display-sm;
    }

    @media screen and (min-width: $breakpoint-md) {
      @include display-lg;
    }

    .sub {
      @include text-sm;

      font-weight: 400;
      color: $black700;
      margin-top: 5px;

      @media screen and (min-width: $breakpoint-sm) {
        @include text-lg;

        margin-top: 10px;
        text-align: center;
      }
    }
  }

  .slider-testimonials {
    margin-top: 32px;
    margin-bottom: 0;

    @media screen and (min-width: $breakpoint-md) {
      @include flex(column, center, center);

      margin-top: 60px;
      margin-bottom: 24px;
    }
  }

  .coach-card {
    margin-top: 15px;

    @media screen and (min-width: $breakpoint-sm) {
      margin-top: 30px;
    }
  }

  .inclusion {
    @include border-radius-sm;

    background-color: $azure-radiance100;
    padding: 10px;
    margin: 35px auto;

    @media screen and (min-width: $breakpoint-sm) {
      padding: 50px;
      margin: 50px auto;

      @include border-radius-xl;
    }

    .inner {
      width: 100%;
      max-width: 500px;
      margin: auto;
      position: relative;

      .details {
        @include flex($direction: column, $justify: center, $align: normal, $gap: 18px);
        @include border-radius-sm;

        background-color: $white;
        padding: 50px 0 30px;
        margin-bottom: 28px;

        @media screen and (min-width: $breakpoint-sm) {
          @include border-radius-xl;
        }

        .top,
        .bottom {
          padding: 0 15px;

          @media screen and (min-width: $breakpoint-sm) {
            padding: 0 30px;
          }
        }

        .top {
          .discount {
            width: fit-content;
            position: absolute;
            right: 0;
            top: 0;
            background-color: $azure-radiance600;
            padding: 4px 12px;
            border-bottom-left-radius: 8px;
            border-top-right-radius: 8px;
            color: $white;
            text-transform: uppercase;
            font-weight: 600;

            @include text-xs;

            @media screen and (min-width: $breakpoint-sm) {
              @include text-md;

              border-top-right-radius: 32px;
              padding: 4px 20px 4px 12px;
            }
          }

          .caption {
            @include text-xl;

            font-weight: 700;

            @media screen and (min-width: $breakpoint-sm) {
              @include display-sm;
            }

            .grey {
              color: $black400;

              @include text-sm;

              font-weight: 500;

              @media screen and (min-width: $breakpoint-sm) {
                @include text-lg;
              }
            }
          }

          .subtitle {
            @include flex($direction: row, $justify: start, $gap: 6px);
            @include text-xs;

            font-weight: 400;

            @media screen and (min-width: $breakpoint-sm) {
              @include text-md;
            }
          }

          .price {
            margin-top: 25px;
            font-size: 24px;
            line-height: 33px;
            font-weight: 700;

            .blue {
              @include text-md;

              font-weight: 600;
              color: $azure-radiance600;
              margin-left: 5px;
              text-decoration: line-through;
            }
          }
        }

        .bottom {
          .include {
            @include text-md;

            font-weight: 700;
          }

          .list-item {
            @include text-sm;

            font-weight: 500;
            margin: 6px 0;

            @media screen and (min-width: $breakpoint-sm) {
              @include text-md;

              margin: 8px 0;
            }

            > img {
              margin-right: 3px;
            }

            .grey {
              font-size: 8px;
              font-weight: 400;
              color: $black500;

              @media screen and (min-width: $breakpoint-sm) {
                @include text-xs;
              }
            }
          }
        }
      }

      .cta {
        .slots {
          @include flex(row, $gap: 6px);

          width: 85%;
          text-align: center;
          margin: 15px auto 0;

          @media screen and (min-width: $breakpoint-sm) {
            width: 70%;
          }
        }
      }
    }
  }

  .pulse-circle {
    width: 10px;
    height: 10px;

    @include border-radius-rounded;

    background-color: $sorbus500;
    position: relative;

    @keyframes pulse {
      0% {
        box-shadow: 0 0 0 0 rgb(255 146 10 / 20%);
      }

      50% {
        box-shadow: 0 0 0 5px rgb(255 146 10 / 20%);
      }

      100% {
        box-shadow: 0 0 0 0 rgb(255 146 10 / 20%);
      }
    }

    &::after {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      background: transparent;

      @include border-radius-rounded;

      z-index: 1;
      animation: pulse 2s forwards infinite;
      animation-timing-function: linear;
    }
  }
}
</style>
